import { AlertTriangle, ChevronDown, Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import useSWR from 'swr';

import { api } from '@/lib/api';
import {
  lessonPackSubjectLabel,
  subjectToLessonPackEnum,
  type LessonPackSubjectEnum,
} from '@/lib/curriculum-subject-map';
import type { CurriculumChapter, CurriculumTopic } from '@/lib/schema.types';
import { cn } from '@/lib/utils';

import { InlineChapterTitleEdit } from './InlineChapterTitleEdit';

export interface CurriculumSelection {
  board: string;
  classNum: number | null;
  grade: string;                 // "Class N" — derived from classNum
  subjectRaw: string;            // free-form, e.g. "Science (Curiosity)"
  subject: LessonPackSubjectEnum; // enum slot the generator accepts
  chapter: string;               // chapter title
  topics: string[];              // up to 3 topic titles (or teacher free-text)
  /** The full chapter doc when one is selected — useful for future
   *  features (Phase 2 RAG content, etc.). Null when manual or unset. */
  chapterDoc: CurriculumChapter | null;
}

interface Props {
  value: CurriculumSelection;
  onChange: (next: CurriculumSelection) => void;
}

/** A lesson pack can target at most this many topics from a chapter. */
const MAX_TOPICS = 3;

export function CurriculumPicker({ value, onChange }: Props) {
  const [manualSubject, setManualSubject] = useState(false);
  const [manualChapter, setManualChapter] = useState(false);
  const [manualTopic, setManualTopic] = useState(false);

  // ----- Cascade fetches (SWR keyed by the upstream selection) -----

  const boardsSWR = useSWR(
    ['/curriculum/boards'],
    () => api.curriculum.listBoards(),
    { revalidateOnFocus: false, dedupingInterval: 1000 * 60 * 60 },
  );

  const classesSWR = useSWR(
    value.board ? ['/curriculum/classes', value.board] : null,
    () => api.curriculum.listClasses({ board: value.board }),
    { revalidateOnFocus: false, dedupingInterval: 1000 * 60 * 60 },
  );

  const subjectsSWR = useSWR(
    value.board && value.classNum
      ? ['/curriculum/subjects', value.board, value.classNum]
      : null,
    () => api.curriculum.listSubjects({ board: value.board, class: value.classNum! }),
    { revalidateOnFocus: false, dedupingInterval: 1000 * 60 * 5 },
  );

  const chaptersSWR = useSWR(
    value.board && value.classNum && value.subjectRaw && !manualSubject
      ? ['/curriculum/chapters', value.board, value.classNum, value.subjectRaw]
      : null,
    () =>
      api.curriculum.listChapters({
        board: value.board,
        class: value.classNum!,
        subject: value.subjectRaw,
      }),
  );

  const chapters = useMemo(() => {
    const list = chaptersSWR.data ?? [];
    return [...list].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
  }, [chaptersSWR.data]);

  const selectedChapter = useMemo<CurriculumChapter | null>(() => {
    if (!value.chapter || manualChapter) return null;
    return chapters.find((c) => c.chapterTitle === value.chapter) ?? value.chapterDoc;
  }, [chapters, value.chapter, value.chapterDoc, manualChapter]);

  const topicsAvailable = selectedChapter?.topics ?? [];

  useEffect(() => {
    if (subjectsSWR.data && subjectsSWR.data.length === 0) setManualSubject(true);
  }, [subjectsSWR.data]);
  useEffect(() => {
    if (
      !manualSubject &&
      chaptersSWR.data &&
      chaptersSWR.data.length === 0
    )
      setManualChapter(true);
  }, [chaptersSWR.data, manualSubject]);
  useEffect(() => {
    if (
      !manualChapter &&
      selectedChapter &&
      topicsAvailable.length === 0
    )
      setManualTopic(true);
  }, [manualChapter, selectedChapter, topicsAvailable.length]);

  // ----- Change handlers (always emit the FULL CurriculumSelection) -----

  const emit = (patch: Partial<CurriculumSelection>) =>
    onChange({ ...value, ...patch });

  const onBoardChange = (board: string) =>
    emit({
      board,
      // Reset downstream — different boards rarely share subjects.
      classNum: null,
      grade: '',
      subjectRaw: '',
      subject: 'general',
      chapter: '',
      topics: [],
      chapterDoc: null,
    });

  const onClassChange = (raw: string) => {
    const classNum = raw ? Number(raw) : null;
    emit({
      classNum,
      grade: classNum ? `Class ${classNum}` : '',
      subjectRaw: '',
      subject: 'general',
      chapter: '',
      topics: [],
      chapterDoc: null,
    });
  };

  const onSubjectPick = (subjectRaw: string) =>
    emit({
      subjectRaw,
      subject: subjectToLessonPackEnum(subjectRaw),
      chapter: '',
      topics: [],
      chapterDoc: null,
    });

  const onSubjectManual = (text: string) =>
    emit({
      subjectRaw: text,
      subject: subjectToLessonPackEnum(text),
      // Don't clobber chapter/topic when typing — teacher may be filling
      // in all three manually in sequence.
    });

  const onChapterPick = (chapterId: string) => {
    const ch = chapters.find((c) => c._id === chapterId) ?? null;
    emit({
      chapter: ch?.chapterTitle ?? '',
      chapterDoc: ch,
      topics: [], // reset topic when chapter changes
    });
    // If we just picked a chapter, the topic should re-evaluate auto-fallback
    setManualTopic(false);
  };

  const onChapterManual = (title: string) =>
    emit({ chapter: title, chapterDoc: null });

  // ----- Topics (multi-select, max 3) -----

  const toggleTopic = (title: string) => {
    const t = title.trim();
    if (!t) return;
    if (value.topics.includes(t)) {
      emit({ topics: value.topics.filter((x) => x !== t) });
    } else if (value.topics.length < MAX_TOPICS) {
      emit({ topics: [...value.topics, t] });
    }
  };

  const removeTopic = (title: string) =>
    emit({ topics: value.topics.filter((x) => x !== title) });

  /** Manual entry: comma-separated, trimmed, de-duped, capped at MAX_TOPICS. */
  const onTopicsManual = (text: string) => {
    const parts = text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    emit({ topics: Array.from(new Set(parts)).slice(0, MAX_TOPICS) });
  };

  // ----- Render -----

  return (
    <div className="space-y-4">
      {/* Board + Class — always dropdowns (closed sets, no manual override) */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Board">
          <Select
            value={value.board}
            options={(boardsSWR.data ?? []).map((b) => ({ value: b, label: b }))}
            onChange={onBoardChange}
            loading={boardsSWR.isLoading}
            placeholder="Select board"
          />
        </Field>

        <Field label="Class">
          <Select
            value={value.classNum ? String(value.classNum) : ''}
            options={(classesSWR.data ?? []).map((n) => ({
              value: String(n),
              label: `Class ${n}`,
            }))}
            onChange={onClassChange}
            loading={classesSWR.isLoading}
            placeholder={value.board ? 'Select class' : 'Pick a board first'}
            disabled={!value.board}
          />
        </Field>
      </div>

      {/* Subject — dropdown OR manual */}
      <Field
        label="Subject"
        rightSlot={
          <ManualToggle
            isManual={manualSubject}
            onToggle={() => setManualSubject((m) => !m)}
            forced={!!subjectsSWR.data && subjectsSWR.data.length === 0}
            forcedReason="No subjects in catalogue for this class yet"
          />
        }
      >
        {manualSubject ? (
          <input
            value={value.subjectRaw}
            onChange={(e) => onSubjectManual(e.target.value)}
            placeholder="e.g. Science (Curiosity)"
            className={inputClass}
          />
        ) : (
          <Select
            value={value.subjectRaw}
            options={(subjectsSWR.data ?? []).map((s) => ({ value: s, label: s }))}
            onChange={onSubjectPick}
            loading={subjectsSWR.isLoading}
            placeholder={value.classNum ? 'Select subject' : 'Pick a class first'}
            disabled={!value.classNum}
          />
        )}
        {value.subjectRaw && (
          <div className="mt-1 text-[10.5px] text-fg-subtle">
            {' '}
            <span className="font-mono text-fg-muted">{value.subject}</span> (
            {lessonPackSubjectLabel(value.subject)})
          </div>
        )}
      </Field>

      {/* Chapter — dropdown OR manual; inline pencil edit on selection */}
      <Field
        label="Chapter"
        rightSlot={
          <ManualToggle
            isManual={manualChapter}
            onToggle={() => setManualChapter((m) => !m)}
            forced={
              !manualSubject &&
              !!chaptersSWR.data &&
              chaptersSWR.data.length === 0
            }
            forcedReason="No chapters in catalogue for this slice yet"
          />
        }
      >
        {manualChapter ? (
          <input
            value={value.chapter}
            onChange={(e) => onChapterManual(e.target.value)}
            placeholder="e.g. Conservation of Plants and Animals"
            className={inputClass}
          />
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Select
                value={selectedChapter?._id ?? ''}
                options={chapters.map((c) => ({
                  value: c._id,
                  label: `${c.chapterNumber ?? '·'}. ${c.chapterTitle}`,
                }))}
                onChange={onChapterPick}
                loading={chaptersSWR.isLoading}
                placeholder={
                  value.subjectRaw ? 'Select chapter' : 'Pick a subject first'
                }
                disabled={!value.subjectRaw}
              />
            </div>
            {selectedChapter && (
              <InlineChapterTitleEdit
                chapterId={selectedChapter._id}
                currentTitle={selectedChapter.chapterTitle}
                alwaysVisible
                onSaved={(newTitle) => {
                  // Optimistic emit + refetch the chapters list.
                  emit({
                    chapter: newTitle,
                    chapterDoc: { ...selectedChapter, chapterTitle: newTitle },
                  });
                  void chaptersSWR.mutate();
                }}
              />
            )}
          </div>
        )}
      </Field>

      {/* Topics — multi-select checklist (max 3) OR manual comma-separated */}
      <Field
        label={`Topics${value.topics.length ? ` · ${value.topics.length}/${MAX_TOPICS}` : ''}`}
        rightSlot={
          // If no chapter selected OR chapter has no topics, manual is the
          // only path — hide the toggle since there's nothing to toggle back to.
          selectedChapter && topicsAvailable.length > 0 ? (
            <ManualToggle
              isManual={manualTopic}
              onToggle={() => setManualTopic((m) => !m)}
            />
          ) : null
        }
      >
        {manualTopic || !selectedChapter || topicsAvailable.length === 0 ? (
          <input
            key={`manual-topics-${value.chapter}`}
            defaultValue={value.topics.join(', ')}
            onChange={(e) => onTopicsManual(e.target.value)}
            placeholder={
              !selectedChapter
                ? 'Pick a chapter or type topics, separated by commas'
                : topicsAvailable.length === 0
                  ? 'No topics in catalogue — type up to 3, separated by commas'
                  : 'e.g. Parallel Lines, Transversals, Angle Pairs'
            }
            className={inputClass}
          />
        ) : (
          <>
            {value.topics.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {value.topics.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-md border border-accent/30 bg-accent-soft/60 py-1 pl-2 pr-1 text-[12px] text-accent"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTopic(t)}
                      title="Remove topic"
                      className="grid h-4 w-4 place-items-center rounded text-accent/70 hover:bg-accent/10 hover:text-accent"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="max-h-44 space-y-0.5 overflow-auto rounded-md border border-border bg-white p-1.5">
              {topicsAvailable.map((t: CurriculumTopic) => {
                const checked = value.topics.includes(t.title);
                const atMax = value.topics.length >= MAX_TOPICS;
                const disabled = !checked && atMax;
                return (
                  <label
                    key={t.title}
                    className={cn(
                      'flex items-center gap-2 rounded px-2 py-1.5 text-[13px]',
                      disabled
                        ? 'cursor-not-allowed opacity-40'
                        : 'cursor-pointer hover:bg-bg-subtle',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleTopic(t.title)}
                      className="accent-accent"
                    />
                    <span className="flex-1 text-fg">
                      {t.number ? `${t.number} ${t.title}` : t.title}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="mt-1 text-[10.5px] text-fg-subtle">
              {value.topics.length >= MAX_TOPICS
                ? `Maximum ${MAX_TOPICS} topics — remove one to pick another.`
                : `Select up to ${MAX_TOPICS} topics for this lesson.`}
            </p>
          </>
        )}
      </Field>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sub-pieces                                                                  */
/* -------------------------------------------------------------------------- */

const inputClass =
  'h-10 w-full rounded-md border border-border bg-white px-3 text-[13.5px] text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20';

function Field({
  label,
  children,
  rightSlot,
}: {
  label: string;
  children: ReactNode;
  rightSlot?: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center justify-between gap-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
        <span>{label}</span>
        {rightSlot}
      </span>
      {children}
    </label>
  );
}

function Select({
  value,
  options,
  onChange,
  placeholder,
  loading,
  disabled,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading}
        className={cn(
          'h-10 w-full appearance-none rounded-md border border-border bg-white pl-3 pr-9 text-[13.5px] text-fg',
          'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20',
          'disabled:cursor-not-allowed disabled:bg-bg-subtle/40 disabled:text-fg-subtle',
        )}
      >
        <option value="">{placeholder ?? 'Select…'}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {loading ? (
        <Loader2
          size={14}
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-fg-subtle"
        />
      ) : (
        <ChevronDown
          size={14}
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-subtle"
        />
      )}
    </div>
  );
}

function ManualToggle({
  isManual,
  onToggle,
  forced,
  forcedReason,
}: {
  isManual: boolean;
  onToggle: () => void;
  /** When true, the catalogue has nothing for this slice — show a small
   *  warning glyph alongside the toggle so teachers know why. */
  forced?: boolean;
  forcedReason?: string;
}) {
  return (
    <span className="flex items-center gap-1.5 normal-case tracking-normal">
      {forced && (
        <span
          title={forcedReason}
          className="inline-flex items-center gap-1 text-amber-700"
        >
          <AlertTriangle size={10} />
        </span>
      )}
      <button
        type="button"
        onClick={onToggle}
        className="text-[10.5px] font-medium text-accent hover:underline"
      >
        {isManual ? '← Use catalogue' : 'Type manually'}
      </button>
    </span>
  );
}
